import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axios";
import { getAuth } from "firebase/auth";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";


function fmtDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("sr-RS");
}

function Stat({ label, value, danger = false }) {
  return (
    <div
      className={`p-3 rounded text-center border ${
        danger
          ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-900"
          : "bg-white text-black border-gray-200 dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
      }`}
    >
      <div className="text-lg font-bold">{value || 0}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

function HealthBadge({ health }) {
  const styles = {
    completed:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-300 dark:border-green-900",
    on_track:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-300 dark:border-blue-900",
    at_risk:
      "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-300 dark:border-yellow-900",
    delayed:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-900",
  };

  const labels = {
    completed: "Completed",
    on_track: "On track",
    at_risk: "At risk",
    delayed: "Delayed",
  };

  return (
    <span className={`px-3 py-1 rounded-full border text-sm font-medium ${styles[health] || styles.on_track}`}>
      {labels[health] || "On track"}
    </span>
  );
}

export default function ProjectDashboardCard({ projectId }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getAuth().currentUser.getIdToken();

        const res = await axios.get(`/project/${projectId}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setStats(res.data);
      } catch (err) {
        console.error("Failed to load project stats:", err);
      }
    };

    if (projectId) load();
  }, [projectId]);

  const dashboard = useMemo(() => {
    if (!stats?.tasks) return null;

    const t = stats.tasks;
    const r = stats.resources || {};

    const total = Number(t.total || 0);
    const todo = Number(t.todo || 0);
    const inProgress = Number(t.in_progress || 0);
    const done = Number(t.done || 0);
    const cancelled = Number(t.cancelled || 0);
    const overdue = Number(t.overdue || 0);

    const progress = total > 0 ? Math.round((done / total) * 100) : 0;

    let health = "on_track";
    if (total > 0 && done === total) health = "completed";
    else if (overdue > 0) health = "delayed";
    else if (inProgress > 0 || todo > 0) health = "at_risk";

    const chartData = [
      { name: "Todo", value: todo },
      { name: "In Progress", value: inProgress },
      { name: "Done", value: done },
      { name: "Cancelled", value: cancelled },
    ].filter((x) => Number(x.value) > 0);

    return {
      tasks: t,
      resources: r,
      progress,
      health,
      chartData,
    };
  }, [stats]);

  if (!dashboard) return null;

  const t = dashboard.tasks;
  const r = dashboard.resources;

  return (
    <div className="mb-6 border border-gray-200 dark:border-neutral-700 rounded-2xl p-4 bg-white dark:bg-neutral-900">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-black dark:text-white">Project Dashboard</h2>
        <HealthBadge health={dashboard.health} />
      </div>

      <div className="text-sm text-gray-500 dark:text-neutral-400">
  {dashboard.health === "completed" && "All tasks are completed."}
  {dashboard.health === "on_track" && "Project is currently on track."}
  {dashboard.health === "at_risk" && "Project still has open work in progress."}
  {dashboard.health === "delayed" && "Project has overdue tasks and needs attention."}
</div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Stat label="Total" value={t.total} />
        <Stat label="Todo" value={t.todo} />
        <Stat label="In Progress" value={t.in_progress} />
        <Stat label="Done" value={t.done} />
        <Stat label="Overdue" value={t.overdue} danger />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-gray-200 dark:border-neutral-700 rounded-2xl p-4">
          <div className="text-sm font-medium text-black dark:text-white mb-2">Progress</div>
          <div className="w-full bg-gray-200 dark:bg-neutral-800 h-4 rounded-full overflow-hidden">
            <div
              className="bg-green-500 h-4 rounded-full transition-all"
              style={{ width: `${dashboard.progress}%` }}
            />
          </div>
          <div className="text-xs mt-2 text-gray-600 dark:text-neutral-300">
            {dashboard.progress}% completed
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
              <div className="text-xs text-gray-500 dark:text-neutral-400">Resources total</div>
              <div className="text-lg font-semibold text-black dark:text-white">{r.total || 0}</div>
            </div>
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
              <div className="text-xs text-gray-500 dark:text-neutral-400">Active</div>
              <div className="text-lg font-semibold text-black dark:text-white">{r.active || 0}</div>
            </div>
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
              <div className="text-xs text-gray-500 dark:text-neutral-400">Planned</div>
              <div className="text-lg font-semibold text-black dark:text-white">{r.planned || 0}</div>
            </div>
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800">
              <div className="text-xs text-gray-500 dark:text-neutral-400">Completed</div>
              <div className="text-lg font-semibold text-black dark:text-white">{r.completed || 0}</div>
            </div>
          </div>

          <div className="mt-4 border border-gray-200 dark:border-neutral-700 rounded-2xl p-4">
  <div className="text-sm font-medium text-black dark:text-white mb-2">
    Last Activity
  </div>
  
  {stats?.summary?.lastActivity ? (
    <div className="space-y-1">
      <div className="text-sm text-black dark:text-white font-medium">
        {stats.summary.lastActivity.title || stats.summary.lastActivity.action_type}
      </div>
      <div className="text-sm text-gray-600 dark:text-neutral-300">
        {stats.summary.lastActivity.message || "-"}
      </div>
      <div className="text-xs text-gray-500 dark:text-neutral-400">
        {(stats.summary.lastActivity.user_name ||
          stats.summary.lastActivity.user_email ||
          "System")}{" "}
        • {fmtDateTime(stats.summary.lastActivity.created_at)}
      </div>
    </div>
  ) : (
    <div className="text-sm text-gray-500 dark:text-neutral-400">
      No activity yet.
    </div>
  )}
</div>
        </div>

        <div className="border border-gray-200 dark:border-neutral-700 rounded-2xl p-4">
          <div className="text-sm font-medium text-black dark:text-white mb-2">Task Distribution</div>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dashboard.chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                >
                  {dashboard.chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.name === "Done"
                          ? "#22c55e"
                          : entry.name === "In Progress"
                          ? "#3b82f6"
                          : entry.name === "Todo"
                          ? "#94a3b8"
                          : "#ef4444"
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>



        </div>
      </div>
    </div>
  );
}